/**
 * BATCH 22 PART 17 — CleverTap Marketing Workflow Templates (201–213)
 * All workflows: CleverTap mobile-first, push as primary channel, 8 nodes each
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 201–213 ============

export const ELITE_BATCH22_PART17: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // 201. "Price Drop Alert" | CleverTap | Pattern D
  "Price Drop Alert": {
    summary:
      "Detects product price drops via CleverTap event webhook, uses AI to extract discount details, verifies user interest and push opt-in, sends immediate push notification for large drops (>20%), waits 24 hours, checks if user purchased, and sends urgency email follow-up with limited-time framing if item remains unbought.",
    nodes: [
      trigger("CleverTap: Price drop event received", "webhook_app", {
        source: "clevertap",
        event: "product.price_dropped",
        howItStarts:
          "Fires when CleverTap detects a price drop on a product a user has viewed or wishlisted",
        setup:
          "Connect CleverTap webhook for product.price_dropped events targeting users with browse or wishlist history",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract price drop details",
        280,
        "Extract product and pricing details from the CleverTap price drop event. Return JSON with product_name, original_price, new_price, discount_percent, product_url, image_url, and category.",
        "Price drop event data: {{{1.body}}}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "original_price", as: "original_price", type: "number" },
          { field: "new_price", as: "new_price", type: "number" },
          { field: "discount_percent", as: "discount_percent", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "image_url", as: "image_url", type: "string" },
        ],
      ),
      n("3", "hitApi", "CleverTap: Verify user interest and push opt-in", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.clevertap.com/1/profile.json",
          headers: [
            { key: "X-CleverTap-Account-Id", value: "{clevertap_account_id}" },
            { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
          variables: [
            {
              name: "record.profileData.push_opt_in",
              customName: "push_opt_in",
              type: "boolean",
            },
            {
              name: "record.profileData.email",
              customName: "user_email",
              type: "string",
            },
            {
              name: "record.profileData.name",
              customName: "user_name",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Check: Discount > 20%?", 760, {
        conditions: [
          {
            field: "{{{2.discount_percent}}}",
            operator: "greater_than",
            value: "20",
          },
        ],
      }),
      n("5", "sendMessage", "CleverTap: Push price drop alert", 1000, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "push",
          to: "{{{1.body.user_id}}}",
          title: "Price Drop Alert: {{{2.product_name}}}",
          body: "Now {{{2.discount_percent}}}% off! Was ${{{2.original_price}}}, now ${{{2.new_price}}}. Tap to grab the deal before it's gone.",
          url: "{{{2.product_url}}}",
          image: "{{{2.image_url}}}",
        },
      }),
      n("6", "delayAction", "Wait 24 hours", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Check: User purchased item?", 1480, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_purchased_{{{2.product_name}}}",
            operator: "equals",
            value: "false",
          },
        ],
      }),
      emailNode(
        "8",
        "Email: Urgency follow-up with expiring discount",
        1720,
        "{{{3.user_email}}}",
        "Last chance: {{{2.product_name}}} is still {{{2.discount_percent}}}% off",
        "Hi {{{3.user_name}}},\n\nThe price drop on {{{2.product_name}}} won't last forever. It's currently ${{{2.new_price}}} (down from ${{{2.original_price}}}).\n\nThis deal could expire at any time — grab it now before the price goes back up.\n\nShop now: {{{2.product_url}}}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // 202. "Back in Stock Alert" | CleverTap | Pattern D
  "Back in Stock Alert": {
    summary:
      "Triggers when CleverTap detects a previously out-of-stock product is restocked, AI extracts product restock details, verifies the user was on the waitlist and has push enabled, checks stock urgency (fewer than 50 units), sends immediate push notification with deep link, waits 36 hours, checks if user purchased, and sends email with similar items if they haven't.",
    nodes: [
      trigger("CleverTap: Product back in stock event", "webhook_app", {
        source: "clevertap",
        event: "product.back_in_stock",
        howItStarts:
          "Fires when a product tracked on a user's waitlist is restocked in inventory",
        setup:
          "Connect CleverTap webhook for product.back_in_stock events targeting users who requested stock alerts",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract restock and product details",
        280,
        "Extract product restock details from the CleverTap event. Return JSON with product_name, product_url, image_url, current_stock, price, category, and waitlist_count.",
        "Back in stock event data: {{{1.body}}}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "image_url", as: "image_url", type: "string" },
          { field: "current_stock", as: "current_stock", type: "number" },
          { field: "price", as: "price", type: "number" },
          { field: "category", as: "category", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "CleverTap: Verify waitlist membership and push opt-in",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profile.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
            variables: [
              {
                name: "record.profileData.push_opt_in",
                customName: "push_opt_in",
                type: "boolean",
              },
              {
                name: "record.profileData.email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "record.profileData.name",
                customName: "user_name",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Limited stock (< 50 units)?", 760, {
        conditions: [
          {
            field: "{{{2.current_stock}}}",
            operator: "less_than",
            value: "50",
          },
        ],
      }),
      n("5", "sendMessage", "CleverTap: Push back-in-stock alert", 1000, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "push",
          to: "{{{1.body.user_id}}}",
          title: "It's Back! {{{2.product_name}}}",
          body: "The item you've been waiting for is back in stock at ${{{2.price}}}. Only {{{2.current_stock}}} left — tap to buy now before it sells out again.",
          url: "{{{2.product_url}}}",
          image: "{{{2.image_url}}}",
        },
      }),
      n("6", "delayAction", "Wait 36 hours", 1240, {
        delayDetails: { duration: 36, unit: "hours" },
      }),
      n("7", "condition", "Check: User still hasn't purchased?", 1480, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_purchased_{{{2.product_name}}}",
            operator: "equals",
            value: "false",
          },
        ],
      }),
      emailNode(
        "8",
        "Email: Similar items and restock reminder",
        1720,
        "{{{3.user_email}}}",
        "{{{2.product_name}}} is still in stock — plus items you might love",
        "Hi {{{3.user_name}}},\n\n{{{2.product_name}}} is back and still available at ${{{2.price}}}. We also picked some similar {{{2.category}}} items you might like.\n\nDon't wait — popular restocks sell out fast.\n\nShop now: {{{2.product_url}}}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // 203. "Geo-Fenced Store Visit Nudge" | CleverTap | Pattern A
  "Geo-Fenced Store Visit Nudge": {
    summary:
      "Triggers when CleverTap detects a user entering a geofenced store zone, AI extracts location context and nearby store details, looks up the user's profile for shopping preferences, checks if they are a loyalty member, sends a location-triggered push with a personalized in-store offer, waits 2 hours, checks if they visited the store, and sends a follow-up push with online alternatives if they didn't visit.",
    nodes: [
      trigger("CleverTap: Geofence entry detected", "webhook_app", {
        source: "clevertap",
        event: "user.geofence_entered",
        howItStarts:
          "Fires when a user's device enters a geofenced zone around a physical store location",
        setup:
          "Configure CleverTap geofence regions around store locations and enable location event webhooks",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract location and store context",
        280,
        "Extract geofence entry details. Return JSON with store_name, store_address, latitude, longitude, nearest_offer, store_hours, and distance_meters.",
        "Geofence event data: {{{1.body}}}",
        [
          { field: "store_name", as: "store_name", type: "string" },
          { field: "store_address", as: "store_address", type: "string" },
          { field: "nearest_offer", as: "nearest_offer", type: "string" },
          { field: "distance_meters", as: "distance_meters", type: "number" },
          { field: "store_hours", as: "store_hours", type: "string" },
        ],
      ),
      n("3", "hitApi", "CleverTap: Fetch user profile and preferences", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.clevertap.com/1/profile.json",
          headers: [
            { key: "X-CleverTap-Account-Id", value: "{clevertap_account_id}" },
            { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
          variables: [
            {
              name: "record.profileData.name",
              customName: "user_name",
              type: "string",
            },
            {
              name: "record.profileData.loyalty_member",
              customName: "loyalty_member",
              type: "boolean",
            },
            {
              name: "record.profileData.preferred_category",
              customName: "preferred_category",
              type: "string",
            },
            {
              name: "record.profileData.email",
              customName: "user_email",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Check: Is loyalty member?", 760, {
        conditions: [
          {
            field: "{{{3.loyalty_member}}}",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      n(
        "5",
        "sendMessage",
        "CleverTap: Push personalized store visit nudge",
        1000,
        {
          sendMessageDetails: {
            service: "clevertap",
            channel: "push",
            to: "{{{1.body.user_id}}}",
            title: "You're near {{{2.store_name}}}!",
            body: "Hi {{{3.user_name}}}, pop in for your exclusive loyalty offer: {{{2.nearest_offer}}}. We're open {{{2.store_hours}}}. You're only {{{2.distance_meters}}}m away!",
            url: "app://store-locator/{{{2.store_name}}}",
          },
        },
      ),
      n("6", "delayAction", "Wait 2 hours", 1240, {
        delayDetails: { duration: 2, unit: "hours" },
      }),
      n("7", "condition", "Check: User visited the store?", 1480, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_store_visit_{{{2.store_name}}}",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      n(
        "8",
        "sendMessage",
        "CleverTap: Push online shopping alternative",
        1720,
        {
          sendMessageDetails: {
            service: "clevertap",
            channel: "push",
            to: "{{{1.body.user_id}}}",
            title: "Couldn't make it? Shop {{{3.preferred_category}}} online",
            body: "No worries! Browse our latest {{{3.preferred_category}}} collection online and enjoy the same offers from {{{2.store_name}}}. Tap to start shopping.",
            url: "app://shop/{{{3.preferred_category}}}",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // 204. "Push Primer / Push Permission Journey" | CleverTap | Pattern A
  "Push Primer / Push Permission Journey": {
    summary:
      "Triggers when a user opens the app without push permissions enabled, AI determines optimal primer timing and message based on user engagement, fetches the user's profile to check session count, shows an in-app push primer if the user has completed 3+ sessions, waits 24 hours, checks if push permission was granted, and sends an in-app message with a softer value-driven permission request if not yet granted.",
    nodes: [
      trigger("CleverTap: App open without push permission", "webhook_app", {
        source: "clevertap",
        event: "app.opened_no_push",
        howItStarts:
          "Fires when a user opens the app and has not yet granted push notification permission",
        setup:
          "Configure CleverTap webhook for app.opened events filtered to users where push_opt_in is false",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Determine optimal primer message and timing",
        280,
        "Analyze user engagement to craft an optimal push permission primer. Return JSON with primer_title, primer_body, value_proposition, and recommended_delay_hours.",
        "User engagement data: {{{1.body}}}",
        [
          { field: "primer_title", as: "primer_title", type: "string" },
          { field: "primer_body", as: "primer_body", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          {
            field: "recommended_delay_hours",
            as: "recommended_delay_hours",
            type: "number",
          },
        ],
      ),
      n("3", "hitApi", "CleverTap: Fetch user profile and session count", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.clevertap.com/1/profile.json",
          headers: [
            { key: "X-CleverTap-Account-Id", value: "{clevertap_account_id}" },
            { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
          variables: [
            {
              name: "record.profileData.name",
              customName: "user_name",
              type: "string",
            },
            {
              name: "record.profileData.session_count",
              customName: "session_count",
              type: "number",
            },
            {
              name: "record.profileData.push_opt_in",
              customName: "push_opt_in",
              type: "boolean",
            },
            {
              name: "record.profileData.email",
              customName: "user_email",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Check: User completed 3+ sessions?", 760, {
        conditions: [
          {
            field: "{{{3.session_count}}}",
            operator: "greater_than_or_equal",
            value: "3",
          },
        ],
      }),
      n("5", "sendMessage", "CleverTap: Show in-app push primer", 1000, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "in_app",
          to: "{{{1.body.user_id}}}",
          title: "{{{2.primer_title}}}",
          body: "{{{2.primer_body}}} {{{2.value_proposition}}}",
          cta: "Enable Notifications",
          url: "app://settings/push-permission",
        },
      }),
      n("6", "delayAction", "Wait 24 hours", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Check: Push permission granted?", 1480, {
        conditions: [
          { field: "{{{3.push_opt_in}}}", operator: "equals", value: "true" },
        ],
      }),
      n(
        "8",
        "sendMessage",
        "CleverTap: Softer in-app permission request",
        1720,
        {
          sendMessageDetails: {
            service: "clevertap",
            channel: "in_app",
            to: "{{{1.body.user_id}}}",
            title: "Stay in the loop, {{{3.user_name}}}",
            body: "Get notified about price drops, flash sales, and order updates. Enable push notifications so you never miss a deal.",
            cta: "Turn On Notifications",
            url: "app://settings/push-permission",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // 205. "In-App Survey Journey" | CleverTap | Pattern I
  "In-App Survey Journey": {
    summary:
      "Triggers when a user reaches a milestone (e.g., 10th session or 5th purchase) via CleverTap event, AI extracts milestone context and generates survey questions, looks up user lifecycle stage and engagement history, checks if the user is an active engaged user (7+ sessions in 30 days), sends an in-app survey, waits 3 days, checks if the survey was completed, and sends a follow-up push with a thank-you reward or a gentle survey reminder.",
    nodes: [
      trigger("CleverTap: User milestone reached", "webhook_app", {
        source: "clevertap",
        event: "user.milestone_reached",
        howItStarts:
          "Fires when a user crosses a meaningful engagement milestone such as 10th session, 5th purchase, or 30-day streak",
        setup:
          "Configure CleverTap webhook for user.milestone_reached events with milestone_type in the payload",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Generate survey questions for milestone",
        280,
        "Generate a contextual in-app survey based on the user milestone. Return JSON with survey_title, question_1, question_2, question_3, milestone_type, and reward_offer.",
        "Milestone event data: {{{1.body}}}",
        [
          { field: "survey_title", as: "survey_title", type: "string" },
          { field: "question_1", as: "question_1", type: "string" },
          { field: "question_2", as: "question_2", type: "string" },
          { field: "milestone_type", as: "milestone_type", type: "string" },
          { field: "reward_offer", as: "reward_offer", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "CleverTap: Lookup user lifecycle and engagement history",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profile.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
            variables: [
              {
                name: "record.profileData.name",
                customName: "user_name",
                type: "string",
              },
              {
                name: "record.profileData.sessions_last_30d",
                customName: "sessions_last_30d",
                type: "number",
              },
              {
                name: "record.profileData.email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "record.profileData.lifecycle_stage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Active engaged user (7+ sessions in 30d)?",
        760,
        {
          conditions: [
            {
              field: "{{{3.sessions_last_30d}}}",
              operator: "greater_than_or_equal",
              value: "7",
            },
          ],
        },
      ),
      n("5", "sendMessage", "CleverTap: Show in-app survey", 1000, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "in_app",
          to: "{{{1.body.user_id}}}",
          title: "{{{2.survey_title}}}",
          body: "Congrats on your {{{2.milestone_type}}} milestone, {{{3.user_name}}}! Take a quick 30-second survey and get {{{2.reward_offer}}} as a thank you.",
          cta: "Start Survey",
          url: "app://survey/{{{2.milestone_type}}}",
        },
      }),
      n("6", "delayAction", "Wait 3 days", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Check: Survey completed?", 1480, {
        conditions: [
          {
            field:
              "{{{1.body.user_id}}}_survey_completed_{{{2.milestone_type}}}",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      n(
        "8",
        "sendMessage",
        "CleverTap: Push thank-you or survey reminder",
        1720,
        {
          sendMessageDetails: {
            service: "clevertap",
            channel: "push",
            to: "{{{1.body.user_id}}}",
            title: "Your feedback matters, {{{3.user_name}}}!",
            body: "Thanks for being a power user! Complete our quick survey to claim your {{{2.reward_offer}}} reward. It only takes 30 seconds.",
            url: "app://survey/{{{2.milestone_type}}}",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // 206. "NPS Detractor Recovery" | CleverTap | Pattern G
  "NPS Detractor Recovery": {
    summary:
      "Triggers on a scheduled daily check for users who submitted NPS scores of 0-6 in the last 24 hours, looks up the detractor's profile including purchase history and recent support tickets, AI analyzes the likely dissatisfaction reason and crafts a recovery plan, checks if the user is a high-value customer (LTV > $500), sends a push notification with a personalized apology and recovery offer, waits 5 days, checks if the user's sentiment improved, and sends a recovery email with a direct CS contact link if still unresolved.",
    nodes: [
      trigger("CleverTap: Daily NPS detractor check", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Runs daily to identify users who submitted NPS scores of 0-6 in the past 24 hours via CleverTap",
        setup:
          "Schedule daily check for CleverTap NPS survey responses where score is 0-6",
        inputFields: [],
      }),
      n("2", "hitApi", "CleverTap: Lookup detractor profile and history", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.clevertap.com/1/profile.json",
          headers: [
            { key: "X-CleverTap-Account-Id", value: "{clevertap_account_id}" },
            { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
          variables: [
            {
              name: "record.profileData.name",
              customName: "user_name",
              type: "string",
            },
            {
              name: "record.profileData.email",
              customName: "user_email",
              type: "string",
            },
            {
              name: "record.profileData.ltv",
              customName: "user_ltv",
              type: "number",
            },
            {
              name: "record.profileData.nps_score",
              customName: "nps_score",
              type: "number",
            },
            {
              name: "record.profileData.last_support_ticket",
              customName: "last_support_ticket",
              type: "string",
            },
            {
              name: "record.profileData.total_purchases",
              customName: "total_purchases",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI: Analyze dissatisfaction reason and craft recovery plan",
        520,
        "Analyze a detractor NPS response and recommend a recovery strategy. Return JSON with likely_reason, recovery_message, recovery_offer, urgency_level, and recommended_channel.",
        "User: {{{2.user_name}}}, NPS: {{{2.nps_score}}}, LTV: ${{{2.user_ltv}}}, purchases: {{{2.total_purchases}}}, last ticket: {{{2.last_support_ticket}}}",
        [
          { field: "likely_reason", as: "likely_reason", type: "string" },
          { field: "recovery_message", as: "recovery_message", type: "string" },
          { field: "recovery_offer", as: "recovery_offer", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),
      n("4", "condition", "Check: High-value customer (LTV > $500)?", 760, {
        conditions: [
          { field: "{{{2.user_ltv}}}", operator: "greater_than", value: "500" },
        ],
      }),
      n(
        "5",
        "sendMessage",
        "CleverTap: Push personalized recovery outreach",
        1000,
        {
          sendMessageDetails: {
            service: "clevertap",
            channel: "push",
            to: "{{{1.body.user_id}}}",
            title: "We hear you, {{{2.user_name}}}",
            body: "{{{3.recovery_message}}} As a valued customer, here's {{{3.recovery_offer}}} to make things right. Tap to connect with our team.",
            url: "app://support/priority",
          },
        },
      ),
      n("6", "delayAction", "Wait 5 days", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Check: User sentiment improved?", 1480, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_sentiment_improved",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      emailNode(
        "8",
        "Email: Direct CS escalation with recovery offer",
        1720,
        "{{{2.user_email}}}",
        "We want to make this right, {{{2.user_name}}}",
        "Hi {{{2.user_name}}},\n\nWe noticed your recent experience wasn't up to par, and we genuinely want to fix that. {{{3.recovery_message}}}\n\nYour dedicated support contact is ready to help resolve this personally. Here's {{{3.recovery_offer}}} as a gesture of our commitment to you.\n\nReply to this email or tap the link below to connect with your dedicated support agent.\n\nContact support: https://app.example.com/support/priority/{{{1.body.user_id}}}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // 207. "Loyalty Tier Upgrade" | CleverTap | Pattern H
  "Loyalty Tier Upgrade": {
    summary:
      "Triggers when CleverTap detects a user has been upgraded to a new loyalty tier, looks up the user's full loyalty profile including new tier perks and points balance, checks if the new tier is Gold or above for VIP treatment, sends a celebratory push notification with new tier benefits, waits 5 days, checks if the user has redeemed any new perks, sends a perks education push if unredeemed, and logs the tier upgrade event to Google Sheets for tracking.",
    nodes: [
      trigger("CleverTap: Loyalty tier upgrade event", "webhook_app", {
        source: "clevertap",
        event: "loyalty.tier_upgraded",
        howItStarts:
          "Fires when a user's loyalty tier is upgraded based on spending or engagement thresholds in CleverTap",
        setup:
          "Connect CleverTap webhook for loyalty.tier_upgraded events with new_tier and previous_tier in payload",
        inputFields: [],
      }),
      n("2", "hitApi", "CleverTap: Fetch loyalty profile and tier perks", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.clevertap.com/1/profile.json",
          headers: [
            { key: "X-CleverTap-Account-Id", value: "{clevertap_account_id}" },
            { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
          variables: [
            {
              name: "record.profileData.name",
              customName: "user_name",
              type: "string",
            },
            {
              name: "record.profileData.email",
              customName: "user_email",
              type: "string",
            },
            {
              name: "record.profileData.loyalty_tier",
              customName: "new_tier",
              type: "string",
            },
            {
              name: "record.profileData.points_balance",
              customName: "points_balance",
              type: "number",
            },
            {
              name: "record.profileData.ltv",
              customName: "user_ltv",
              type: "number",
            },
          ],
        },
      }),
      n("3", "condition", "Check: Gold tier or above?", 520, {
        conditions: [
          {
            field: "{{{2.new_tier}}}",
            operator: "is_one_of",
            value: "Gold,Platinum,Diamond",
          },
        ],
      }),
      n("4", "sendMessage", "CleverTap: Push tier upgrade celebration", 760, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "push",
          to: "{{{1.body.user_id}}}",
          title: "You've reached {{{2.new_tier}}} status!",
          body: "Congratulations {{{2.user_name}}}! You've unlocked {{{2.new_tier}}} tier with exclusive perks: free shipping, early access, and priority support. You have {{{2.points_balance}}} points ready to use!",
          url: "app://loyalty/tier-benefits/{{{2.new_tier}}}",
        },
      }),
      n("5", "delayAction", "Wait 5 days", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("6", "condition", "Check: User redeemed any new perks?", 1240, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_perks_redeemed",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      n("7", "sendMessage", "CleverTap: Push perks education reminder", 1480, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "push",
          to: "{{{1.body.user_id}}}",
          title: "Don't miss your {{{2.new_tier}}} perks!",
          body: "Hi {{{2.user_name}}}, you have {{{2.points_balance}}} points and exclusive {{{2.new_tier}}} benefits waiting. Tap to explore free shipping, early access sales, and more.",
          url: "app://loyalty/redeem",
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log tier upgrade event",
        1720,
        "LoyaltyTierUpgrades",
        `[["{{{1.body.user_id}}}","{{{2.user_name}}}","{{{2.new_tier}}}","{{{2.points_balance}}}","{{{2.user_ltv}}}","${new Date().toISOString()}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // 208. "Referral Invite Journey" | CleverTap | Pattern J
  "Referral Invite Journey": {
    summary:
      "Triggers after a user completes a purchase, CleverTap looks up their profile to check purchase count and referral eligibility, checks if they are eligible and haven't been asked recently (3+ purchases, no referral ask in 30 days), sends a push notification with a personalized referral link and incentive offer, waits 5 days, checks if the referral was shared, sends a reminder push if not yet shared, and logs the referral campaign status to Google Sheets.",
    nodes: [
      trigger("CleverTap: Purchase completed event", "webhook_app", {
        source: "clevertap",
        event: "order.completed",
        howItStarts:
          "Fires when a user completes a purchase, triggering referral eligibility evaluation",
        setup:
          "Connect CleverTap webhook for order.completed events to evaluate post-purchase referral opportunity",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "CleverTap: Lookup purchase history and referral eligibility",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profile.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
            variables: [
              {
                name: "record.profileData.name",
                customName: "user_name",
                type: "string",
              },
              {
                name: "record.profileData.email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "record.profileData.total_purchases",
                customName: "total_purchases",
                type: "number",
              },
              {
                name: "record.profileData.last_referral_ask",
                customName: "last_referral_ask",
                type: "string",
              },
              {
                name: "record.profileData.referral_code",
                customName: "referral_code",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "condition",
        "Check: Eligible (3+ purchases, no recent ask)?",
        520,
        {
          conditions: [
            {
              field: "{{{2.total_purchases}}}",
              operator: "greater_than_or_equal",
              value: "3",
            },
          ],
        },
      ),
      n(
        "4",
        "sendMessage",
        "CleverTap: Push referral invite with incentive",
        760,
        {
          sendMessageDetails: {
            service: "clevertap",
            channel: "push",
            to: "{{{1.body.user_id}}}",
            title: "Share the love, {{{2.user_name}}}!",
            body: "Give your friends $15 off their first order and you'll get $15 credit too. Share your code: {{{2.referral_code}}}. Tap to invite friends now!",
            url: "app://referral/share/{{{2.referral_code}}}",
          },
        },
      ),
      n("5", "delayAction", "Wait 5 days", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("6", "condition", "Check: Referral link shared?", 1240, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_referral_shared",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      n("7", "sendMessage", "CleverTap: Push referral reminder", 1480, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "push",
          to: "{{{1.body.user_id}}}",
          title: "Your $15 referral credit is waiting",
          body: "Don't forget — share code {{{2.referral_code}}} with friends and earn $15 for each person who orders. No limit on earnings! Tap to share.",
          url: "app://referral/share/{{{2.referral_code}}}",
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log referral campaign status",
        1720,
        "ReferralCampaigns",
        `[["{{{1.body.user_id}}}","{{{2.user_name}}}","{{{2.referral_code}}}","{{{2.total_purchases}}}","pending","${new Date().toISOString()}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // 209. "New Arrivals Announcement" | CleverTap | Pattern D
  "New Arrivals Announcement": {
    summary:
      "Triggers when CleverTap receives a new product catalog event for a category, AI extracts product collection details and generates compelling copy, verifies user preference match and push opt-in via CleverTap profile, checks if the new arrivals match the user's preferred category, sends push notification showcasing new products with deep link, waits 48 hours, checks if the user browsed the new arrivals, and sends a curated email with the full new arrivals collection if not yet browsed.",
    nodes: [
      trigger("CleverTap: New arrivals published event", "webhook_app", {
        source: "clevertap",
        event: "catalog.new_arrivals",
        howItStarts:
          "Fires when new products are added to a category the user has browsed or purchased from",
        setup:
          "Connect CleverTap webhook for catalog.new_arrivals events targeting users with matching category affinity",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract new arrivals details and generate copy",
        280,
        "Extract new arrivals collection details and write compelling push/email copy. Return JSON with collection_name, category, hero_product, hero_image_url, product_count, collection_url, push_headline, and email_intro.",
        "New arrivals event data: {{{1.body}}}",
        [
          { field: "collection_name", as: "collection_name", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "hero_product", as: "hero_product", type: "string" },
          { field: "hero_image_url", as: "hero_image_url", type: "string" },
          { field: "product_count", as: "product_count", type: "number" },
          { field: "collection_url", as: "collection_url", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "CleverTap: Verify user category preference and push opt-in",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profile.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
            variables: [
              {
                name: "record.profileData.name",
                customName: "user_name",
                type: "string",
              },
              {
                name: "record.profileData.email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "record.profileData.preferred_category",
                customName: "preferred_category",
                type: "string",
              },
              {
                name: "record.profileData.push_opt_in",
                customName: "push_opt_in",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: Category matches user preference?", 760, {
        conditions: [
          {
            field: "{{{2.category}}}",
            operator: "equals",
            value: "{{{3.preferred_category}}}",
          },
        ],
      }),
      n("5", "sendMessage", "CleverTap: Push new arrivals alert", 1000, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "push",
          to: "{{{1.body.user_id}}}",
          title: "Just dropped: {{{2.collection_name}}}",
          body: "{{{2.product_count}}} new {{{2.category}}} items just arrived including {{{2.hero_product}}}. Be the first to shop the collection!",
          url: "{{{2.collection_url}}}",
          image: "{{{2.hero_image_url}}}",
        },
      }),
      n("6", "delayAction", "Wait 48 hours", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("7", "condition", "Check: User browsed new arrivals?", 1480, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_browsed_{{{2.collection_name}}}",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      emailNode(
        "8",
        "Email: Full new arrivals collection showcase",
        1720,
        "{{{3.user_email}}}",
        "New in {{{2.category}}}: {{{2.product_count}}} fresh arrivals curated for you",
        "Hi {{{3.user_name}}},\n\nOur latest {{{2.collection_name}}} collection just dropped with {{{2.product_count}}} new products in {{{2.category}}}.\n\nHighlight: {{{2.hero_product}}}\n\nBrowse the full collection and find your next favorite — many items are already selling fast.\n\nShop new arrivals: {{{2.collection_url}}}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // 210. "RFM VIP Retention Journey" | CleverTap | Pattern H
  "RFM VIP Retention Journey": {
    summary:
      "Triggers on a weekly schedule to identify users with top-tier RFM scores in CleverTap, looks up their full VIP profile including recent activity and lifetime value, checks if the user is in the top 5% by LTV (>$2000), sends exclusive VIP push notification with early access and personal perks, waits 7 days, checks if the VIP redeemed the exclusive offer, sends a high-touch perks reminder push if unredeemed, and logs the VIP retention touchpoint to Google Sheets.",
    nodes: [
      trigger("CleverTap: Weekly RFM VIP segment check", "schedule", {
        schedule: "Every Monday at 10:00 AM",
        howItStarts:
          "Runs weekly to identify and nurture users in the top RFM (Recency, Frequency, Monetary) segment via CleverTap",
        setup:
          "Schedule weekly CleverTap RFM segment export for users scoring 5-5-5 or 5-5-4 in RFM model",
        inputFields: [],
      }),
      n("2", "hitApi", "CleverTap: Fetch VIP profile, LTV, and activity", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.clevertap.com/1/profile.json",
          headers: [
            { key: "X-CleverTap-Account-Id", value: "{clevertap_account_id}" },
            { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
          variables: [
            {
              name: "record.profileData.name",
              customName: "user_name",
              type: "string",
            },
            {
              name: "record.profileData.email",
              customName: "user_email",
              type: "string",
            },
            {
              name: "record.profileData.ltv",
              customName: "user_ltv",
              type: "number",
            },
            {
              name: "record.profileData.rfm_score",
              customName: "rfm_score",
              type: "string",
            },
            {
              name: "record.profileData.loyalty_tier",
              customName: "loyalty_tier",
              type: "string",
            },
            {
              name: "record.profileData.last_purchase_date",
              customName: "last_purchase_date",
              type: "string",
            },
          ],
        },
      }),
      n("3", "condition", "Check: Top 5% VIP (LTV > $2000)?", 520, {
        conditions: [
          {
            field: "{{{2.user_ltv}}}",
            operator: "greater_than",
            value: "2000",
          },
        ],
      }),
      n("4", "sendMessage", "CleverTap: Push exclusive VIP treatment", 760, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "push",
          to: "{{{1.body.user_id}}}",
          title: "VIP Exclusive: Just for you, {{{2.user_name}}}",
          body: "As one of our most valued {{{2.loyalty_tier}}} members, you get 48-hour early access to our new collection plus free express shipping on every order this month.",
          url: "app://vip/exclusive-access",
        },
      }),
      n("5", "delayAction", "Wait 7 days", 1000, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("6", "condition", "Check: VIP redeemed exclusive offer?", 1240, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_vip_offer_redeemed",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      n("7", "sendMessage", "CleverTap: Push VIP perks reminder", 1480, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "push",
          to: "{{{1.body.user_id}}}",
          title: "Your VIP perks are expiring soon",
          body: "Hi {{{2.user_name}}}, your exclusive {{{2.loyalty_tier}}} early access and free express shipping expire in 48 hours. Don't miss out — tap to shop your VIP collection.",
          url: "app://vip/exclusive-access",
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log VIP retention touchpoint",
        1720,
        "VIPRetention",
        `[["{{{1.body.user_id}}}","{{{2.user_name}}}","{{{2.loyalty_tier}}}","{{{2.rfm_score}}}","{{{2.user_ltv}}}","${new Date().toISOString()}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // 211. "App Uninstall Rescue" | CleverTap | Pattern G
  "App Uninstall Rescue": {
    summary:
      "Triggers on a daily schedule to identify users who uninstalled the app in the past 24 hours via CleverTap's uninstall tracking, looks up the user's profile including last activity and purchase history, AI analyzes likely uninstall reason and crafts a personalized rescue message, checks if the user is a high-value customer (3+ purchases), sends a rescue email with a compelling reason to reinstall, waits 7 days, checks if the user reinstalled, and sends a final email with a strong incentive offer if still uninstalled.",
    nodes: [
      trigger("CleverTap: Daily app uninstall detection", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to identify users who uninstalled the app in the last 24 hours via CleverTap uninstall tracking",
        setup:
          "Schedule daily CleverTap segment export for users with app_uninstalled event in the past 24 hours",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "CleverTap: Lookup uninstalled user profile and history",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profile.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
            variables: [
              {
                name: "record.profileData.name",
                customName: "user_name",
                type: "string",
              },
              {
                name: "record.profileData.email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "record.profileData.total_purchases",
                customName: "total_purchases",
                type: "number",
              },
              {
                name: "record.profileData.last_active_date",
                customName: "last_active_date",
                type: "string",
              },
              {
                name: "record.profileData.preferred_category",
                customName: "preferred_category",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Analyze uninstall reason and craft rescue message",
        520,
        "Analyze why a user likely uninstalled the app and craft a compelling rescue email. Return JSON with likely_reason, rescue_subject, rescue_body, incentive_offer, and reinstall_cta.",
        "User: {{{2.user_name}}}, purchases: {{{2.total_purchases}}}, last active: {{{2.last_active_date}}}, preferred: {{{2.preferred_category}}}",
        [
          { field: "likely_reason", as: "likely_reason", type: "string" },
          { field: "rescue_subject", as: "rescue_subject", type: "string" },
          { field: "rescue_body", as: "rescue_body", type: "string" },
          { field: "incentive_offer", as: "incentive_offer", type: "string" },
        ],
      ),
      n("4", "condition", "Check: High-value user (3+ purchases)?", 760, {
        conditions: [
          {
            field: "{{{2.total_purchases}}}",
            operator: "greater_than_or_equal",
            value: "3",
          },
        ],
      }),
      emailNode(
        "5",
        "Email: Personalized app reinstall rescue",
        1000,
        "{{{2.user_email}}}",
        "{{{3.rescue_subject}}}",
        "Hi {{{2.user_name}}},\n\n{{{3.rescue_body}}}\n\nWe've been working on making things better — faster checkout, better recommendations in {{{2.preferred_category}}}, and smoother navigation.\n\nReinstall and get {{{3.incentive_offer}}} on your next order as our way of saying we want you back.\n\nDownload the app: https://app.example.com/download",
      ),
      n("6", "delayAction", "Wait 7 days", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Check: User reinstalled the app?", 1480, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_app_reinstalled",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      emailNode(
        "8",
        "Email: Final rescue with stronger incentive",
        1720,
        "{{{2.user_email}}}",
        "We miss you, {{{2.user_name}}} — here's an exclusive offer",
        "Hi {{{2.user_name}}},\n\nWe haven't seen you in a while and we want to make it worth your while to come back.\n\nHere's an exclusive 25% off your entire next order — no minimum purchase required. Plus free shipping.\n\nThis offer expires in 72 hours and is only available in the app.\n\nReinstall now: https://app.example.com/download?promo=COMEBACK25",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // 212. "Feature Discovery Nudge" | CleverTap | Pattern A
  "Feature Discovery Nudge": {
    summary:
      "Triggers when CleverTap identifies an active user who hasn't used a key feature after 7+ days of activity, AI determines which unused feature would benefit the user most and generates tutorial content, fetches the user's feature usage profile, checks if they have used the app 10+ times without discovering the target feature, shows an in-app feature tutorial nudge, waits 48 hours, checks if the user tried the feature, and sends a push with a video tutorial link if they still haven't engaged.",
    nodes: [
      trigger("CleverTap: Active user missing key feature", "webhook_app", {
        source: "clevertap",
        event: "user.feature_gap_detected",
        howItStarts:
          "Fires when CleverTap identifies an active user who has not used a key app feature after 7+ days of activity",
        setup:
          "Configure CleverTap engagement webhook for users with 7+ active days who haven't triggered key feature events",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Identify best feature to promote and generate tutorial",
        280,
        "Analyze user behavior to determine which unused feature would provide the most value. Return JSON with feature_name, feature_benefit, tutorial_title, tutorial_steps, video_url, and priority_score.",
        "User activity data: {{{1.body}}}",
        [
          { field: "feature_name", as: "feature_name", type: "string" },
          { field: "feature_benefit", as: "feature_benefit", type: "string" },
          { field: "tutorial_title", as: "tutorial_title", type: "string" },
          { field: "tutorial_steps", as: "tutorial_steps", type: "string" },
          { field: "video_url", as: "video_url", type: "string" },
        ],
      ),
      n("3", "hitApi", "CleverTap: Fetch user feature usage profile", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.clevertap.com/1/profile.json",
          headers: [
            { key: "X-CleverTap-Account-Id", value: "{clevertap_account_id}" },
            { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
          variables: [
            {
              name: "record.profileData.name",
              customName: "user_name",
              type: "string",
            },
            {
              name: "record.profileData.app_sessions",
              customName: "app_sessions",
              type: "number",
            },
            {
              name: "record.profileData.features_used",
              customName: "features_used",
              type: "string",
            },
            {
              name: "record.profileData.email",
              customName: "user_email",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Check: 10+ sessions without target feature?", 760, {
        conditions: [
          {
            field: "{{{3.app_sessions}}}",
            operator: "greater_than_or_equal",
            value: "10",
          },
        ],
      }),
      n("5", "sendMessage", "CleverTap: In-app feature discovery nudge", 1000, {
        sendMessageDetails: {
          service: "clevertap",
          channel: "in_app",
          to: "{{{1.body.user_id}}}",
          title: "{{{2.tutorial_title}}}",
          body: "Hi {{{3.user_name}}}, did you know about {{{2.feature_name}}}? {{{2.feature_benefit}}} Here's how to get started: {{{2.tutorial_steps}}}",
          cta: "Try It Now",
          url: "app://feature/{{{2.feature_name}}}",
        },
      }),
      n("6", "delayAction", "Wait 48 hours", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("7", "condition", "Check: User tried the feature?", 1480, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_used_{{{2.feature_name}}}",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      n(
        "8",
        "sendMessage",
        "CleverTap: Push video tutorial for feature",
        1720,
        {
          sendMessageDetails: {
            service: "clevertap",
            channel: "push",
            to: "{{{1.body.user_id}}}",
            title: "Quick tip: {{{2.feature_name}}} in 60 seconds",
            body: "Watch a quick video showing how {{{2.feature_name}}} can {{{2.feature_benefit}}}. Most users say it saves them time every week. Tap to watch!",
            url: "{{{2.video_url}}}",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // 213. "WhatsApp Order Update Fallback" | CleverTap | Pattern E
  "WhatsApp Order Update Fallback": {
    summary:
      "Triggers when an order status changes (shipped, out-for-delivery, delivered) via CleverTap event, AI extracts order and shipping details, looks up user communication preferences and WhatsApp opt-in status, checks if the user has WhatsApp opt-in, sends order update via WhatsApp with tracking link, waits 4 hours, checks if the WhatsApp message was delivered, and falls back to email with the same tracking details if WhatsApp delivery failed.",
    nodes: [
      trigger("CleverTap: Order status changed event", "webhook_app", {
        source: "clevertap",
        event: "order.status_changed",
        howItStarts:
          "Fires when an order status changes to shipped, out-for-delivery, or delivered in CleverTap",
        setup:
          "Connect CleverTap webhook for order.status_changed events covering shipping lifecycle updates",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract order and shipping details",
        280,
        "Extract order status update details for messaging. Return JSON with order_id, status, tracking_url, carrier, estimated_delivery, product_summary, and delivery_address_city.",
        "Order status event data: {{{1.body}}}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "status", as: "order_status", type: "string" },
          { field: "tracking_url", as: "tracking_url", type: "string" },
          { field: "carrier", as: "carrier", type: "string" },
          {
            field: "estimated_delivery",
            as: "estimated_delivery",
            type: "string",
          },
          { field: "product_summary", as: "product_summary", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "CleverTap: Lookup user preferences and WhatsApp opt-in",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profile.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "identity", value: "{{{1.body.user_id}}}" }],
            variables: [
              {
                name: "record.profileData.name",
                customName: "user_name",
                type: "string",
              },
              {
                name: "record.profileData.email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "record.profileData.phone",
                customName: "user_phone",
                type: "string",
              },
              {
                name: "record.profileData.whatsapp_opt_in",
                customName: "whatsapp_opt_in",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Check: User has WhatsApp opt-in?", 760, {
        conditions: [
          {
            field: "{{{3.whatsapp_opt_in}}}",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      n(
        "5",
        "sendMessage",
        "CleverTap: WhatsApp order update with tracking",
        1000,
        {
          sendMessageDetails: {
            service: "clevertap",
            channel: "whatsapp",
            to: "{{{3.user_phone}}}",
            template: "order_status_update",
            body: "Hi {{{3.user_name}}}, your order #{{{2.order_id}}} is now {{{2.order_status}}}.\n\nItems: {{{2.product_summary}}}\nCarrier: {{{2.carrier}}}\nEstimated delivery: {{{2.estimated_delivery}}}\n\nTrack your order: {{{2.tracking_url}}}",
          },
        },
      ),
      n("6", "delayAction", "Wait 4 hours", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      n("7", "condition", "Check: WhatsApp message delivered?", 1480, {
        conditions: [
          {
            field: "{{{1.body.user_id}}}_whatsapp_delivered_{{{2.order_id}}}",
            operator: "equals",
            value: "true",
          },
        ],
      }),
      emailNode(
        "8",
        "Email: Fallback order update with tracking",
        1720,
        "{{{3.user_email}}}",
        "Order #{{{2.order_id}}} update: {{{2.order_status}}}",
        "Hi {{{3.user_name}}},\n\nHere's an update on your order #{{{2.order_id}}}:\n\nStatus: {{{2.order_status}}}\nItems: {{{2.product_summary}}}\nCarrier: {{{2.carrier}}}\nEstimated delivery: {{{2.estimated_delivery}}}\n\nTrack your package: {{{2.tracking_url}}}\n\nIf you have any questions about your delivery, our support team is here to help.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },
};
